import React from 'react'
import { Chrome, ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface BrowserExtensionPromoProps {
  className?: string
}

const CHROME_STORE_URL = 'https://chromewebstore.google.com/detail/ping-river-monitor/bpefndejdlhjlbildchfpehbniddggno'
const EDGE_STORE_URL = 'https://microsoftedge.microsoft.com/addons/detail/dhinpjjnpglpkdmgaohogoiffofkbfkh'

const BrowserExtensionPromo: React.FC<BrowserExtensionPromoProps> = ({ className = '' }) => {
  const { t } = useTranslation()

  // Only render on desktop (SSR-safe check)
  const [isDesktop, setIsDesktop] = React.useState<boolean>(typeof window === 'undefined' ? false : window.innerWidth >= 640)
  const [browser, setBrowser] = React.useState<'edge' | 'chrome' | 'other'>(typeof navigator === 'undefined' ? 'other' : (navigator.userAgent.includes('Edg/') ? 'edge' : (navigator.userAgent.includes('Chrome') || navigator.userAgent.includes('CriOS') ? 'chrome' : 'other')))
  React.useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 640)
    const detect = () => {
      const ua = navigator.userAgent
      if (ua.includes('Edg/')) return setBrowser('edge')
      if (ua.includes('Chrome') || ua.includes('CriOS')) return setBrowser('chrome')
      return setBrowser('other')
    }
    window.addEventListener('resize', onResize)
    detect()
    return () => window.removeEventListener('resize', onResize)
  }, [])

  if (!isDesktop) return null

  return (
    <div className={`bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg border border-amber-200 dark:border-amber-700 ${className}`}>
      <div className="p-4 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <Chrome className="h-6 w-6 text-orange-500" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100">{t('extension.title')}</h2>
              <p className="text-sm text-gray-700 dark:text-gray-200 mt-0.5">{t('extension.subtitle')}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 max-w-3xl">{t('extension.description')}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{t('extension.availableOnDesktop')}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {browser === 'edge' && (
                  <a
                    href={EDGE_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 text-xs sm:text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    title={t('extension.openStore') || 'Open store page'}
                  >
                    <ExternalLink className="h-4 w-4 mr-1.5" />
                    {t('extension.installEdge')}
                  </a>
                )}
                {browser === 'chrome' && (
                  <a
                    href={CHROME_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 text-xs sm:text-sm bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
                    title={t('extension.openStore') || 'Open store page'}
                  >
                    <Chrome className="h-4 w-4 mr-1.5" />
                    {t('extension.installChrome')}
                  </a>
                )}
                {browser === 'other' && (
                  <>
                    <a
                      href={CHROME_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1.5 text-xs sm:text-sm bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
                      title={t('extension.openStore') || 'Open store page'}
                    >
                      <Chrome className="h-4 w-4 mr-1.5" />
                      {t('extension.installChrome')}
                    </a>
                    <a
                      href={EDGE_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1.5 text-xs sm:text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                      title={t('extension.openStore') || 'Open store page'}
                    >
                      <ExternalLink className="h-4 w-4 mr-1.5" />
                      {t('extension.installEdge')}
                    </a>
                  </>
                )}
                {/* Secondary store links */}
                <a
                  href={CHROME_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-1.5 text-xs sm:text-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                  {t('extension.chromeWebStore')}
                </a>
                <a
                  href={EDGE_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-1.5 text-xs sm:text-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                  {t('extension.edgeStore')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BrowserExtensionPromo


